import { UserListModal } from '@/components/UserListModal';
import { useParams } from 'react-router-dom';
import type { UseQueryResult } from '@tanstack/react-query';
import type { WidgetTableProps } from '@/components/WidgetTable/types';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { useToolUsers } from '@/hooks/api/useShadowAiTools';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { usersEndpointsLabel } from '@/components/shared/shadow-ai/usersEndpointsLabel';
import type { ShadowAiToolUsersSource } from '@kirin/api-client/contracts/shadow-ai';

interface AIToolUsageUserListProps {
  toolName: string;
  toolId: string;
  /** KIR-5281: the widget row's category + source, so the list is its users. */
  category?: string;
  source?: ShadowAiToolUsersSource;
  onClose: () => void;
  open: boolean;
}

interface AIToolUser {
  userId: string;
  email: string | null;
  name: string | null;
  machineId: string;
  endpointName?: string | null;
  timestamp: string;
}

// KIR-5281: rows are per user per endpoint, so the endpoint is a column.
const buildColumns = (
  endpointEnabled: boolean
): WidgetTableProps<AIToolUser>['columns'] => [
  {
    header: 'User',
    id: 'user',
    cell: user => (
      <div>
        <div className="font-medium">{user.name || 'Unknown User'}</div>
        {user.email && (
          <div className="text-sm text-muted-foreground">{user.email}</div>
        )}
      </div>
    ),
  },
  {
    header: endpointEnabled ? 'Endpoint' : 'Machine',
    id: 'endpoint',
    cell: user => (
      <span className="font-mono text-sm">
        {user.endpointName || user.machineId}
      </span>
    ),
  },
  {
    header: 'Last Seen',
    id: 'lastSeen',
    cell: user => <RelativeTimeWithTooltip date={user.timestamp} />,
  },
];

export const AIToolUsageUserList = ({
  toolName,
  toolId,
  category,
  source,
  onClose,
  open,
}: AIToolUsageUserListProps) => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const rawQuery = useToolUsers(organizationId, toolId, open, undefined, {
    category,
    source,
  });
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
  ]);
  const endpointEnabled =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;

  // Transform the query to extract just the users array
  // Similar to MCPUsageUserList pattern
  const query = {
    ...rawQuery,
    data: rawQuery.data?.users ?? [],
  } as unknown as UseQueryResult<AIToolUser[]>;

  return (
    <UserListModal<AIToolUser>
      open={open}
      title={
        <>
          Users with <span className="font-mono">{toolName}</span>
          {query.data && query.data.length > 0 && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              {usersEndpointsLabel(query.data)}
            </span>
          )}
        </>
      }
      onOpenChange={isOpen => {
        if (!isOpen) {
          onClose();
        }
      }}
      query={query}
      columns={buildColumns(endpointEnabled)}
    />
  );
};
